import { IPROP_MAP, modifyProps } from '../../generators/modifyProps';
import {
  convertTextToComponent,
  resolvePropObjectKeys
} from '../../utils/componenthelpers';

import { ButtonProps } from '../jdsbutton/logic';
import { CTA } from '../jdscontentblock/interface';
import { Devices } from '../../service/Devices';
import { JDSModalDesktopProps } from './generated';
import { ListBlockProps } from '../jdslistblock/logic';
import { Modal as MobileModal } from '../jdsmodalmobile/generated';
import { useEscapeClose } from '../../hooks/useEscapeClose';

const legacySizes = ['s', 'm'] as const;
type legacySizesType = (typeof legacySizes)[number];
export interface ModalProps
  extends Omit<
    JDSModalDesktopProps,
    'size' | 'primaryCTA' | 'secondaryCTA' | 'header' | '_onModalClick'
  > {
  /**
   * Sets size of Modal
   */
  size?: legacySizesType | JDSModalDesktopProps['size'];
  /**
   * Sets Header as text or as react element
   * passing listblock is deprecated. Always pass a string
   */
  header?: string | ListBlockProps;
  /**
   * Label of primary button. You can also pass a custom LinkType for more granular control. <br/>
   * E.g. `primaryCTA={title:'Delete', {button: { state: 'destructive' } }}`
   */
  primaryCTA?:
    | string
    | {
        title: string;
        onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
        /**
         * @deprecated
         */
        _button?: ButtonProps;
      };
  /**
   * Label of secondary button.You can also pass a custom LinkType for more granular control. <br/>
   * E.g. `secondaryCTA={title:'Delete', {button: { state: 'destructive' } }}`
   */
  secondaryCTA?:
    | string
    | {
        title: string;
        onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
        /**
         * @deprecated
         */
        _button?: ButtonProps;
      };
  /**
   * Can be set to close modal programatically
   * @deprecated use `close` prop
   */
  closed?: boolean;
  /**
   * Pass custom callback on closing modal
   * @deprecated use `onClose`
   */
  onCloseCallback?: () => void;
  /**
   * Function which determines if modal can be closed.
   * If it is false, the modal does not close if clicking on close button or esc
   */
  onRequestClose?: () => boolean;
  /**
   * @deprecated
   * use `isCloseable` prop
   */
  isClosable?: boolean;
}

export const PROP_MAP: IPROP_MAP<ModalProps> = (props) => {
  if (legacySizes.includes(props.size as legacySizesType)) {
    console.warn('these values for the size prop are deprecated');
  }
  if (typeof props.header === 'object') {
    console.warn(
      'passing listblock to the header is deprecated. Please pass a string'
    );
  }
  return {
    onCloseCallback: {
      properties: {
        onClose: props.onCloseCallback,
        onCloseCallback: props.onCloseCallback
      }
    },
    onClose: {
      properties: {
        onCloseCallback: props.onClose
      }
    },
    primaryCTA: {
      properties: {
        primaryCTA: resolvePropObjectKeys(props.primaryCTA, 'title', ''),
        onPrimaryClick:
          (props.primaryCTA as CTA)?.onClick || props.onPrimaryClick
      }
    },
    secondaryCTA: {
      properties: {
        secondaryCTA: resolvePropObjectKeys(props.secondaryCTA, 'title', ''),
        onSecondaryClick:
          (props.secondaryCTA as CTA)?.onClick || props.onSecondaryClick
      }
    },
    isClosable: {
      properties: {
        isCloseable:
          typeof props.isCloseable === 'undefined'
            ? props.isClosable
            : props.isCloseable
      }
    },
    size: {
      variations: {
        s: {
          size: 'small'
        },
        m: {
          size: 'medium'
        }
      }
    },
    children: {
      properties: {
        children: convertTextToComponent(props.children)
      }
    }
  };
};

export const withLogic =
  (Component: React.ComponentType<ModalProps>): React.FC<ModalProps> =>
  (userProps: ModalProps) => {
    const props = modifyProps(userProps, PROP_MAP);

    const {
      onRequestClose,
      onCloseCallback,
      kind = 'dialog',
      isClosable,
      close: userCloseProp,
      closed,
      ...rest
    } = props;

    const closeProp =
      typeof userCloseProp === 'undefined' ? closed : userCloseProp;

    const { mobile } = Devices.useMedia();

    const allowClose = !onRequestClose || onRequestClose();
    const close = () => {
      if (allowClose) {
        onCloseCallback?.();
        rest?.onClose?.();
      }
    };

    useEscapeClose(close, kind === 'informational');

    const handleClose = () => {
      if (kind === 'informational') {
        close();
      }
    };

    if (mobile) {
      return <MobileModal {...userProps} />;
    }

    return (
      <Component
        {...{
          ...rest,
          kind,
          _onModalClick: (ev: React.MouseEvent<HTMLDivElement>) =>
            ev.stopPropagation(),
          close: closeProp,
          _onCancel: close,
          onClose: handleClose
        }}
      />
    );
  };
